import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, CalendarRange, Columns2, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { TooltipProvider } from "../ui/tooltip";
import { HintTooltip } from "./HintTooltip";
import { useLanguage } from "../../contexts/LanguageContext";
import { useMediaQuery } from "../../lib/useMediaQuery";

/**
 * Side dock of the ProgramBuilder.
 *
 * There is exactly ONE dock, hosting one of two tenants at a time (AI assistant
 * or reference panel). Keeping them mutually exclusive avoids a third column
 * squeezing the editor and means the coach only learns one spatial model.
 *
 * Two layouts, by viewport:
 *  - >= 1280px: inline column beside the editor, width draggable and persisted.
 *  - < 1280px: right Sheet overlay. Two dense panels side by side are unreadable
 *    below that width, and the overlay doubles as the "one at a time" mode —
 *    the clipboard is what carries content between the two views.
 */

const DOCK_WIDTH_KEY = "kraveon:pb-dock-width";
const SPLIT_MEDIA_QUERY = "(min-width: 1280px)";
const MIN_DOCK_WIDTH = 340;
const DEFAULT_DOCK_WIDTH = 440;
const MAX_DOCK_WIDTH = 720;

const clampDockWidth = (value) => {
	const viewportCap =
		typeof window === "undefined"
			? MAX_DOCK_WIDTH
			: Math.max(MIN_DOCK_WIDTH, Math.round(window.innerWidth * 0.55));
	const upper = Math.min(MAX_DOCK_WIDTH, viewportCap);
	return Math.round(Math.max(MIN_DOCK_WIDTH, Math.min(value, upper)));
};

const readStoredDockWidth = () => {
	try {
		const stored = Number(localStorage.getItem(DOCK_WIDTH_KEY));
		if (Number.isFinite(stored) && stored > 0) return clampDockWidth(stored);
	} catch {
		// Fall through to the default.
	}
	return DEFAULT_DOCK_WIDTH;
};

/** `true` while the viewport is wide enough for a real side-by-side split. */
export function useIsSplitViewport() {
	return useMediaQuery(SPLIT_MEDIA_QUERY);
}

export function BuilderDock({
	open,
	tab,
	aiAvailable = true,
	agendaAvailable = false,
	onTabChange,
	onClose,
	children,
}) {
	const { t } = useLanguage();
	const isSplitViewport = useIsSplitViewport();
	const [width, setWidth] = useState(readStoredDockWidth);
	const widthRef = useRef(width);

	useEffect(() => {
		widthRef.current = width;
	}, [width]);

	// The cap depends on the viewport, so a resize can invalidate a stored width.
	useEffect(() => {
		const handleResize = () => setWidth((prev) => clampDockWidth(prev));
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const persistWidth = useCallback(() => {
		try {
			localStorage.setItem(DOCK_WIDTH_KEY, String(widthRef.current));
		} catch {
			// Width simply resets to the default on the next load.
		}
	}, []);

	const handleResizeStart = useCallback(
		(event) => {
			event.preventDefault();
			const startX = event.clientX;
			const startWidth = widthRef.current;

			// Dragging left (smaller clientX) widens the dock, hence the inversion.
			const handleMove = (moveEvent) => {
				setWidth(clampDockWidth(startWidth + (startX - moveEvent.clientX)));
			};
			const handleUp = () => {
				document.removeEventListener("pointermove", handleMove);
				document.removeEventListener("pointerup", handleUp);
				document.body.style.userSelect = "";
				persistWidth();
			};

			document.body.style.userSelect = "none";
			document.addEventListener("pointermove", handleMove);
			document.addEventListener("pointerup", handleUp);
		},
		[persistWidth],
	);

	const handleResizeKeyDown = useCallback(
		(event) => {
			const step = event.shiftKey ? 48 : 16;
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				setWidth((prev) => clampDockWidth(prev + step));
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				setWidth((prev) => clampDockWidth(prev - step));
			} else {
				return;
			}
			// Persist on the next frame, once the state update has landed.
			requestAnimationFrame(persistWidth);
		},
		[persistWidth],
	);

	if (!open) return null;

	// Durante il rework AI (docs/ai-program/10-byok-and-mcp-rework.md) il dock
	// ha un solo inquilino: la striscia resta, cosi' il coach vede comunque
	// dove si trova, ma non offre una scheda che non puo' rispondere.
	// The agenda tab (season planning, docs/program-builder-season-strip) only
	// exists where the season strip does: saved, dated, individual program.
	const tabs = [
		{ value: "reference", label: t("pbDockReference"), icon: Columns2 },
		...(agendaAvailable
			? [{ value: "agenda", label: t("pbDockAgenda"), icon: CalendarRange }]
			: []),
		...(aiAvailable ? [{ value: "ai", label: t("pbDockAi"), icon: Bot }] : []),
	];

	const header = (
		<div className="flex items-center gap-1 px-2 py-2 border-b border-border shrink-0">
			{tabs.map((item) => {
				const Icon = item.icon;
				const isActive = tab === item.value;
				return (
					<button
						key={item.value}
						type="button"
						onClick={() => onTabChange(item.value)}
						aria-pressed={isActive}
						className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-sm text-xs font-heading uppercase tracking-wide transition-colors ${
							isActive
								? "bg-volt/10 text-volt border border-volt/40"
								: "text-muted-foreground border border-transparent hover:text-foreground hover:bg-secondary/60"
						}`}
					>
						<Icon className="w-3.5 h-3.5" />
						{item.label}
					</button>
				);
			})}
			{isSplitViewport && (
				<div className="ml-auto">
					<HintTooltip label={t("pbDockClose")}>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={onClose}
							aria-label={t("pbDockClose")}
							className="h-8 w-8 rounded-sm text-muted-foreground hover:text-foreground"
						>
							<X className="w-4 h-4" />
						</Button>
					</HintTooltip>
				</div>
			)}
		</div>
	);

	if (!isSplitViewport) {
		return (
			<TooltipProvider delayDuration={250}>
				<Sheet
					open={open}
					onOpenChange={(next) => {
						if (!next) onClose();
					}}
				>
					<SheetContent
						side="right"
						className="bg-card border-border w-[min(94vw,560px)] sm:max-w-none p-0 flex flex-col gap-0"
					>
						{/* Radix requires a title/description for the dialog a11y contract;
					    the visible affordance is the tab strip below. */}
						<SheetTitle className="sr-only">
							{tab === "ai" ? t("pbDockAi") : t("pbDockReference")}
						</SheetTitle>
						<SheetDescription className="sr-only">
							{t("pbDockReference")}
						</SheetDescription>
						{/* The Sheet primitive renders its own close button top-right; the
					    dock header keeps the tabs reachable in the same spot. */}
						<div className="pr-8">{header}</div>
						<div className="flex-1 min-h-0 overflow-hidden">{children}</div>
					</SheetContent>
				</Sheet>
			</TooltipProvider>
		);
	}

	return (
		<TooltipProvider delayDuration={250}>
			<div className="relative shrink-0 self-start flex" style={{ width }}>
				<div
					className="fixed top-16 right-0 bottom-[2.1rem] z-40 flex"
					style={{ width }}
					data-testid="builder-dock"
				>
					<HintTooltip label={t("pbDockResize")} side="left">
						<div
							role="separator"
							aria-orientation="vertical"
							aria-label={t("pbDockResize")}
							tabIndex={0}
							onPointerDown={handleResizeStart}
							onKeyDown={handleResizeKeyDown}
							className="w-1.5 shrink-0 cursor-col-resize bg-border/60 hover:bg-volt/60 focus:bg-volt/60 focus:outline-none transition-colors"
						/>
					</HintTooltip>
					<div className="flex-1 min-w-0 flex flex-col border-l border-border bg-card">
						{header}
						<div className="flex-1 min-h-0 overflow-hidden">{children}</div>
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
}
